import React, { useMemo, useState } from "react";
import {
  HeightRestriction,
  WidthRestriction,
} from "../../../interfaces/Restrictions";
import IGroup from "@appsinti/common-utils/interfaces/IGroup";
import styled from "styled-components";
import { StatsSummaryApp } from "@appsinti/common-utils/interfaces/IApp";
import GroupFlexContainer from "./GroupFlexContainer";
import MainCardContainer from "../../containers/MainCardContainer";
import CustomLink from "@appsinti/ui-core/CustomLink";
import EmptyCard from "./EmptyCard";
import Group from "./Group";
import CombinedLocationsHeader from "./CombinedLocationsHeader";
import { useDateProvider } from "@appsinti/date-provider";

export const GroupLink = styled(CustomLink)`
  max-height: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const getHeightString = (groupsIds: any[], gap: number, footerHeight: number | null) => {
  let percentage = '32';
  let gapOffset = `${(gap * 2) / 3}`;
  let footerDivider = 3;

  if (groupsIds.length === 2) {
    percentage = '50';
    gapOffset = `${gap / 2}`;
    footerDivider = 2;
  } else if (groupsIds.length === 3) {
    percentage = '33.333';
    gapOffset = `${(gap * 2) / 3}`;
    footerDivider = 3;
  } else if (groupsIds.length === 4) {
    percentage = '50';
    gapOffset = `${gap / 2}`;
    footerDivider = 2;
  } else if (groupsIds.length === 5 || groupsIds.length === 6) {
    percentage = '33.333';
    gapOffset = `${(gap * 2) / 3}`;
    footerDivider = 3;
  }

  return `calc(${percentage}% - ${gapOffset}px - ${footerHeight ? (footerHeight + gap) / footerDivider : 0}px)`;
};

const ConditionalGroupCard: React.FC<{
  app: StatsSummaryApp;
  groups: { [key: number]: IGroup; };
  groupId: number;
}> = ({ app, groups, groupId }) => {
  const heightRestriction: HeightRestriction = useMemo(() => {
    const groupsIds = Object.keys(groups);
    if (groupsIds.length === 2) return "half";
    if (groupsIds.length === 3) return "third";
    if (groupsIds.length === 4) return "half";
    if (groupsIds.length > 4) return "third";
    return "half";
  }, [groups]);

  const widthRestriction: WidthRestriction = useMemo(() => {
    const groupsIds = Object.keys(groups);
    if (groupsIds.length < 4) return "full-width";
    return "half-width";
  }, [groups]);

  if (app.config.groups[groupId] === undefined) {
    return (
      <EmptyCard
        groups={groups}
        groupId={groupId}
        widthRestriction={widthRestriction}
      />
    );
  }

  return (
    <MainCardContainer
      app={app}
      groups={groups}
      groupId={groupId}
      widthRestriction={widthRestriction}
      heightRestriction={heightRestriction}
    >
      <Group
        heightRestriction={heightRestriction}
        widthRestriction={widthRestriction}
        groupId={groupId}
      />
    </MainCardContainer>
  );
};

const Body: React.FC<{
  app: StatsSummaryApp;
  groups: { [key: number]: IGroup; };
  gap: number;
}> = ({ app, groups, gap }) => {
  const groupsIds = Object.keys(groups);
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);

  const heightString = getHeightString(groupsIds, gap, headerHeight);

  const { isFutureDate } = useDateProvider();

  return (
    <>
      {!isFutureDate && <CombinedLocationsHeader setHeaderHeight={setHeaderHeight} headerHeight={headerHeight} gap={gap} />}
      {groupsIds.map((id) => {
        return (
          <GroupFlexContainer
            key={id}
            background={"white"}
            noPadding={true}
            className={`stats-summary-group-flex-container ${groupsIds.length > 3 ? "half-width" : "full-width"}`}
            heightString={heightString}
            body={
              <GroupLink to={`/${id}`}>
                <ConditionalGroupCard
                  app={app}
                  groups={groups}
                  groupId={parseInt(id)}
                />
              </GroupLink>
            }
          />
        );
      })}
    </>
  );
};

export default Body;
